import React, { useEffect, useState } from 'react'
import { Checkbox, Dropdown, Input } from 'antd'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import { MagnifyingGlass, ArrowsDownUp } from '@phosphor-icons/react'
import Down from 'helper/Icons/DownIcon'

interface Props {
  genderFilter: 'male' | 'female' | undefined
  minAge: number | undefined
  maxAge: number | undefined
  orgFilter: string | undefined
  divisionFilter: string | undefined
  teamFilter: string | undefined
  sortDir: 'asc' | 'desc'
  onGenderChange: (v: 'male' | 'female' | undefined) => void
  onAgeChange: (min: number | undefined, max: number | undefined) => void
  onOrgChange: (v: string | undefined) => void
  onDivisionChange: (v: string | undefined) => void
  onTeamChange: (v: string | undefined) => void
  onSortToggle: () => void
  orgOptions: string[]
  divisionOptions: string[]
  teamOptions: string[]
}

const GENDER_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
]

const AttendanceFilters: React.FC<Props> = ({
  genderFilter,
  minAge,
  maxAge,
  orgFilter,
  divisionFilter,
  teamFilter,
  sortDir,
  onGenderChange,
  onAgeChange,
  onOrgChange,
  onDivisionChange,
  onTeamChange,
  onSortToggle,
  orgOptions,
  divisionOptions,
  teamOptions,
}) => {
  const [open, setOpen] = useState(0)

  // pending states
  const [pendingGender, setPendingGender] = useState<'male' | 'female' | undefined>(undefined)
  const [pendingMinAge, setPendingMinAge] = useState<string>('')
  const [pendingMaxAge, setPendingMaxAge] = useState<string>('')
  const [pendingOrg, setPendingOrg] = useState<string | undefined>(undefined)
  const [pendingDiv, setPendingDiv] = useState<string | undefined>(undefined)
  const [pendingTeam, setPendingTeam] = useState<string | undefined>(undefined)

  const [orgSearch, setOrgSearch] = useState('')
  const [divSearch, setDivSearch] = useState('')
  const [teamSearch, setTeamSearch] = useState('')

  // sync pending from applied when dropdown opens
  useEffect(() => {
    if (open === 1) {
      setPendingMinAge(minAge !== undefined ? String(minAge) : '')
      setPendingMaxAge(maxAge !== undefined ? String(maxAge) : '')
    }
    if (open === 2) setPendingGender(genderFilter)
    if (open === 3) { setPendingOrg(orgFilter); setOrgSearch('') }
    if (open === 4) { setPendingDiv(divisionFilter); setDivSearch('') }
    if (open === 5) { setPendingTeam(teamFilter); setTeamSearch('') }
  }, [open])

  const filteredOrgs = orgOptions.filter(o => o.toLowerCase().includes(orgSearch.toLowerCase()))
  const filteredDivs = divisionOptions.filter(d => d.toLowerCase().includes(divSearch.toLowerCase()))
  const filteredTeams = teamOptions.filter(t => t.toLowerCase().includes(teamSearch.toLowerCase()))

  // ── Age & Birthday ───────────────────────────────────────────────────────
  const ageItems: ItemType[] = [
    {
      key: 'age-content',
      label: (
        <>
          <div className="filter-body">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input
                type="number"
                placeholder="Min age"
                value={pendingMinAge}
                onChange={e => setPendingMinAge(e.target.value)}
                style={{ width: '50%' }}
              />
              <span style={{ color: '#939598', flexShrink: 0 }}>–</span>
              <Input
                type="number"
                placeholder="Max age"
                value={pendingMaxAge}
                onChange={e => setPendingMaxAge(e.target.value)}
                style={{ width: '50%' }}
              />
            </div>
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => {
                setPendingMinAge('')
                setPendingMaxAge('')
                onAgeChange(undefined, undefined)
                setOpen(0)
              }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => {
                onAgeChange(
                  pendingMinAge ? Number(pendingMinAge) : undefined,
                  pendingMaxAge ? Number(pendingMaxAge) : undefined,
                )
                setOpen(0)
              }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  // ── Gender ───────────────────────────────────────────────────────────────
  const genderItems: ItemType[] = [
    {
      key: 'gender-content',
      label: (
        <>
          <div className="filter-body">
            <Checkbox.Group
              className="upd-checkbox-group"
              value={pendingGender ? [pendingGender] : []}
              onChange={vals =>
                setPendingGender(vals[vals.length - 1] as 'male' | 'female' | undefined)
              }
            >
              {GENDER_OPTIONS.map(({ label, value }) => (
                <Checkbox key={value} value={value}>{label}</Checkbox>
              ))}
            </Checkbox.Group>
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => { setPendingGender(undefined); onGenderChange(undefined); setOpen(0) }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => { onGenderChange(pendingGender); setOpen(0) }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  // ── Organization ─────────────────────────────────────────────────────────
  const orgItems: ItemType[] = [
    {
      key: 'org-content',
      label: (
        <>
          <div className="filter-body">
            <Input
              placeholder="Search..."
              value={orgSearch}
              onChange={e => setOrgSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            {filteredOrgs.length === 0 ? (
              <p className="upd-filter-empty">No organizations found</p>
            ) : (
              <Checkbox.Group
                className="upd-checkbox-group"
                value={pendingOrg ? [pendingOrg] : []}
                onChange={vals => setPendingOrg(vals[vals.length - 1] as string | undefined)}
              >
                {filteredOrgs.map(org => (
                  <Checkbox key={org} value={org}>{org}</Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => { setPendingOrg(undefined); onOrgChange(undefined); setOpen(0) }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => { onOrgChange(pendingOrg); setOpen(0) }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  // ── Division ─────────────────────────────────────────────────────────────
  const divItems: ItemType[] = [
    {
      key: 'div-content',
      label: (
        <>
          <div className="filter-body">
            <Input
              placeholder="Search..."
              value={divSearch}
              onChange={e => setDivSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            {filteredDivs.length === 0 ? (
              <p className="upd-filter-empty">No divisions found</p>
            ) : (
              <Checkbox.Group
                className="upd-checkbox-group"
                value={pendingDiv ? [pendingDiv] : []}
                onChange={vals => setPendingDiv(vals[vals.length - 1] as string | undefined)}
              >
                {filteredDivs.map(div => (
                  <Checkbox key={div} value={div}>{div}</Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => { setPendingDiv(undefined); onDivisionChange(undefined); setOpen(0) }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => { onDivisionChange(pendingDiv); setOpen(0) }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  // ── Team ─────────────────────────────────────────────────────────────────
  const teamItems: ItemType[] = [
    {
      key: 'team-content',
      label: (
        <>
          <div className="filter-body">
            <Input
              placeholder="Search..."
              value={teamSearch}
              onChange={e => setTeamSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            {filteredTeams.length === 0 ? (
              <p className="upd-filter-empty">No teams found</p>
            ) : (
              <Checkbox.Group
                className="upd-checkbox-group"
                value={pendingTeam ? [pendingTeam] : []}
                onChange={vals => setPendingTeam(vals[vals.length - 1] as string | undefined)}
              >
                {filteredTeams.map(team => (
                  <Checkbox key={team} value={team}>{team}</Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => { setPendingTeam(undefined); onTeamChange(undefined); setOpen(0) }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => { onTeamChange(pendingTeam); setOpen(0) }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  const hasAge = minAge !== undefined || maxAge !== undefined
  const ageLabel = hasAge
    ? `${minAge ?? '0'}–${maxAge ?? '∞'} yrs`
    : 'Age & Birthday'

  return (
    <div className="att-filter-bar">
      <div className="att-filter-left">
        <span className="att-filter-label">Filter by:</span>

        <button className="att-icon-btn">
          <MagnifyingGlass size={18} color="#000" />
        </button>

        {/* Age & Birthday */}
        <Dropdown
          menu={{ items: ageItems }}
          placement="bottomLeft"
          arrow={false}
          open={open === 1}
          onOpenChange={isOpen => setOpen(isOpen ? 1 : 0)}
          overlayClassName="custom-dropdown-overlay"
          overlayStyle={{ width: '240px' }}
        >
          <button
            className={`${open === 1 || hasAge ? 'filter-btn-click' : 'filter-btn'} w-500`}
            onClick={() => setOpen(open === 1 ? 0 : 1)}
          >
            {ageLabel} <Down />
          </button>
        </Dropdown>

        {/* Gender */}
        <Dropdown
          menu={{ items: genderItems }}
          placement="bottomLeft"
          arrow={false}
          open={open === 2}
          onOpenChange={isOpen => setOpen(isOpen ? 2 : 0)}
          overlayClassName="custom-dropdown-overlay"
          overlayStyle={{ width: '200px' }}
        >
          <button
            className={`${open === 2 || genderFilter ? 'filter-btn-click' : 'filter-btn'} w-500`}
            onClick={() => setOpen(open === 2 ? 0 : 2)}
          >
            {genderFilter ? (genderFilter === 'male' ? 'Male' : 'Female') : 'Gender'} <Down />
          </button>
        </Dropdown>

        {/* Organization */}
        <Dropdown
          menu={{ items: orgItems }}
          placement="bottomLeft"
          arrow={false}
          open={open === 3}
          onOpenChange={isOpen => setOpen(isOpen ? 3 : 0)}
          overlayClassName="custom-dropdown-overlay"
          overlayStyle={{ width: '260px' }}
        >
          <button
            className={`${open === 3 || orgFilter ? 'filter-btn-click' : 'filter-btn'} w-500`}
            onClick={() => setOpen(open === 3 ? 0 : 3)}
          >
            {orgFilter ?? 'Organization'} <Down />
          </button>
        </Dropdown>

        {/* Division */}
        <Dropdown
          menu={{ items: divItems }}
          placement="bottomLeft"
          arrow={false}
          open={open === 4}
          onOpenChange={isOpen => setOpen(isOpen ? 4 : 0)}
          overlayClassName="custom-dropdown-overlay"
          overlayStyle={{ width: '260px' }}
        >
          <button
            className={`${open === 4 || divisionFilter ? 'filter-btn-click' : 'filter-btn'} w-500`}
            onClick={() => setOpen(open === 4 ? 0 : 4)}
          >
            {divisionFilter ?? 'Division'} <Down />
          </button>
        </Dropdown>

        {/* Team */}
        <Dropdown
          menu={{ items: teamItems }}
          placement="bottomLeft"
          arrow={false}
          open={open === 5}
          onOpenChange={isOpen => setOpen(isOpen ? 5 : 0)}
          overlayClassName="custom-dropdown-overlay"
          overlayStyle={{ width: '260px' }}
        >
          <button
            className={`${open === 5 || teamFilter ? 'filter-btn-click' : 'filter-btn'} w-500`}
            onClick={() => setOpen(open === 5 ? 0 : 5)}
          >
            {teamFilter ?? 'Team'} <Down />
          </button>
        </Dropdown>
      </div>

      <div className="att-filter-right">
        <span className="att-filter-label">Sort by:</span>
        <button
          className={`filter-btn w-500 att-sort-pill`}
          onClick={onSortToggle}
        >
          First Name
          <ArrowsDownUp size={14} color="#000" />
          <span className="att-sort-dir">{sortDir === 'asc' ? '↑' : '↓'}</span>
        </button>
      </div>
    </div>
  )
}

export default AttendanceFilters
